import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import crowLogo from "../assets/crow-logo.png";
import { Facebook } from "lucide-react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    {
      name: "Services",
      path: "/services",
      children: [
        {
          name: "Metal Fabrication & Welding",
          path: "/services/metal-fabrication",
        },
        {
          name: "Turnkey Manufacturing",
          path: "/services/turnkey-manufacturing",
        },
        { name: "CNC Precision Machining", path: "/services/cnc-machining" },
        { name: "Warehousing & Supply Chain", path: "/services/warehousing" },
        { name: "Repair & Refurbishment", path: "/services/repair" },
        { name: "Hydro Static Testing & NDE", path: "/services/testing" },
        { name: "Product Development", path: "/services/development" },
      ],
    },
    {
      name: "Products",
      path: "/products",
      children: [
        { name: "BOP Control Systems", path: "/products/bop-systems" },
        { name: "Frac Accumulator Systems", path: "/products/frac-systems" },
        { name: "Depurators", path: "/products/depurators" },
        { name: "Oilfield Skids", path: "/products/oilfield-skids" },
        {
          name: "Structural Weldments",
          path: "/products/structural-weldments",
        },
        { name: "Cabinets & Sheetmetal", path: "/products/cabinets" },
        { name: "Spools & Piping", path: "/products/spools" },
      ],
    },
    {
      name: "Industries",
      path: "/industries",
      children: [
        { name: "Oilfield", path: "/industries/oilfield" },
        { name: "Midstream & Process", path: "/industries/midstream" },
        { name: "Data Centers", path: "/industries/data-centers" },
        { name: "Infrastructure", path: "/industries/infrastructure" },
      ],
    },
    {
      name: "Equipment",
      path: "/equipment",
      children: [
        { name: "Equipment List", path: "/equipment/list" },
        { name: "Quality Certification", path: "/equipment/certification" },
      ],
    },
    { name: "Contact", path: "/contact" },
  ];

  const handleDropdownToggle = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-sm"
        }`}
      data-testid="main-header"
    >
      <div className="bg-blue-900 text-white py-2 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between text-sm font-semibold">

          <a
            href="tel:+18006422769"
            className="flex items-center space-x-2 hover:text-blue-200 transition-colors"
            data-testid="header-phone"
          >
            <Phone className="w-4 h-4 text-white" />
            <span className="font-bold">800.642.2769</span>
          </a>

          <div className="flex items-center space-x-4">
            <a
              href="mailto:quote@crowcorp.com"
              className="flex items-center space-x-2 hover:text-blue-200 transition-colors mr-0 lg:mr-5"
              data-testid="header-email"
            >
              <span className="text-base">✉️</span>
              <span className="font-bold tracking-wide">quote@crowcorp.com</span>
            </a>

            <a
              href="https://www.facebook.com/crowcorporation"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-sm hover:bg-blue-100 hover:scale-105 transition-all"
            >
              <Facebook className="w-4 h-4 text-blue-700" />
            </a>
          </div>

        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link
            to="/"
            className="flex items-center space-x-3"
            data-testid="logo-link"
          >
            <div className="w-12 h-12 rounded-lg flex items-center justify-center">
              <img
                src={crowLogo}
                alt="CrowCorp Logo"
                className="w-19 h-19 object-contain"
              />
            </div>

            <div className="flex flex-col leading-tight">
              <div className="text-xl font-bold text-gray-700 heading-font tracking-tight">
                CROW
              </div>

              <div className="text-sm text-gray-700 tracking-[0.2em] -mt-1 font-bold">
                CORPORATION
              </div>

              <div className="text-[11px] text-black tracking-wide mt-[2px] font-medium">
                A Windlass Company
              </div>
            </div>
          </Link>

          <nav
            className="hidden lg:flex items-center space-x-1"
            data-testid="desktop-nav"
          >
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() =>
                  link.children && setActiveDropdown(link.name)
                }
                onMouseLeave={() => link.children && setActiveDropdown(null)}
              >
                {link.children ? (
                  <button
                    className="px-4 py-2 text-gray-700 hover:text-blue-700 font-medium text-sm nav-link flex items-center space-x-1"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span>{link.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ) : (
                  <Link
                    to={link.path}
                    className="px-4 py-2 text-gray-700 hover:text-blue-700 font-medium text-sm nav-link block"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.name}
                  </Link>
                )}

                {link.children && activeDropdown === link.name && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 dropdown-menu">
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                        data-testid={`dropdown-${child.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <Link
            to="/contact"
            className="hidden lg:block px-6 py-2.5 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-medium text-sm transition-colors shadow-md hover:shadow-lg"
            data-testid="cta-quote-button"
          >
            Request Quote
          </Link>

          <button
            className="lg:hidden p-2 text-gray-700 hover:text-blue-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div
          className="lg:hidden bg-white border-t border-gray-100"
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.children ? (
                  <div>
                    <button
                      onClick={() => handleDropdownToggle(link.name)}
                      className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span>{link.name}</span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${activeDropdown === link.name ? "rotate-180" : ""
                          }`}
                      />
                    </button>
                    {activeDropdown === link.name && (
                      <div className="ml-4 mt-2 space-y-1">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="block px-4 py-2 text-sm text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                            data-testid={`mobile-dropdown-${child.name.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <Link
              to="/contact"
              className="block w-full text-center px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-medium mt-4"
              data-testid="mobile-cta-quote-button"
            >
              Request Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
